import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link2, Unlink, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react'
import api from '../../services/api'
import MendeleyConnect from '../../components/mendeley/MendeleyConnect'
import MendeleyImport from '../../components/mendeley/MendeleyImport'

interface MendeleyStatus {
  connected: boolean
  last_sync?: string
  email?: string
  library_count?: number
}

export default function MendeleySync() {
  const queryClient = useQueryClient()
  const [disconnectConfirm, setDisconnectConfirm] = useState(false)

  const { data: status, isLoading: statusLoading } = useQuery<MendeleyStatus>({
    queryKey: ['mendeleyStatus'],
    queryFn: async () => {
      const { data } = await api.get('/academic/mendeley/status')
      return data
    },
  })

  const connectMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/academic/mendeley/connect')
      return data
    },
    onSuccess: (data: { auth_url?: string }) => {
      if (data.auth_url) {
        window.location.href = data.auth_url
      }
      queryClient.invalidateQueries({ queryKey: ['mendeleyStatus'] })
    },
  })

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      await api.delete('/academic/mendeley/connect')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mendeleyStatus'] })
      setDisconnectConfirm(false)
    },
  })

  if (statusLoading) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="animate-pulse">
          <div className="h-7 bg-gray-200 rounded w-48 mb-4" />
          <div className="h-4 bg-gray-200 rounded w-80 mb-8" />
          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <div className="h-5 bg-gray-200 rounded w-40 mb-4" />
            <div className="h-4 bg-gray-200 rounded w-64 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-48" />
          </div>
        </div>
      </div>
    )
  }

  const isConnected = status?.connected ?? false

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Mendeley Integration</h1>
        <p className="text-gray-500 mt-1">
          Connect your Mendeley account to import and export research references
        </p>
      </div>

      {!isConnected ? (
        <MendeleyConnect
          onConnect={() => connectMutation.mutate()}
          isConnecting={connectMutation.isPending}
          error={connectMutation.isError ? 'Failed to initiate connection. Please try again.' : undefined}
        />
      ) : (
        <div className="space-y-6">
          {/* Connection Status Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                  <Link2 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Mendeley Connected</h2>
                  {status?.email && (
                    <p className="text-sm text-gray-500">{status.email}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right text-sm">
                  {status?.last_sync && (
                    <p className="text-gray-500">
                      Last synced: {new Date(status.last_sync).toLocaleDateString()}{' '}
                      {new Date(status.last_sync).toLocaleTimeString()}
                    </p>
                  )}
                  {status?.library_count !== undefined && (
                    <p className="text-gray-400">{status.library_count} items in library</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Import Section */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <ArrowDownToLine className="h-5 w-5 text-acad-600" />
              <h2 className="text-lg font-semibold text-gray-900">Import from Mendeley</h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Select documents from your Mendeley library to import into the academic portal.
              Imported resources will be searchable and can be added to your lists.
            </p>
            <MendeleyImport />
          </div>

          {/* Export Section */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <ArrowUpFromLine className="h-5 w-5 text-acad-600" />
              <h2 className="text-lg font-semibold text-gray-900">Export to Mendeley</h2>
            </div>
            <p className="text-sm text-gray-500 mb-2">
              You can export any resource to your Mendeley library directly from its detail page.
            </p>
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 text-sm text-gray-600">
              <ol className="list-decimal list-inside space-y-1.5">
                <li>Browse or search for a resource in the portal</li>
                <li>Open the resource detail page</li>
                <li>
                  Click the <span className="font-medium text-acad-600">&ldquo;Export to Mendeley&rdquo;</span> button
                </li>
                <li>The resource will be added to your Mendeley library</li>
              </ol>
            </div>
          </div>

          {/* Disconnect Section */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-900">Disconnect Mendeley</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Remove the connection to your Mendeley account. Previously imported resources will
                  remain in the portal.
                </p>
              </div>
              {!disconnectConfirm ? (
                <button
                  onClick={() => setDisconnectConfirm(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                >
                  <Unlink className="h-4 w-4" />
                  Disconnect
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setDisconnectConfirm(false)}
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => disconnectMutation.mutate()}
                    disabled={disconnectMutation.isPending}
                    className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    {disconnectMutation.isPending ? 'Disconnecting...' : 'Confirm Disconnect'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
